// @flow
import * as React from 'react';
import connectWS from 'speed-js-react/src/websocket/connectWS';
import WebSocketClient from 'speed-js-core/src/websocket/WebSocketClient';
import { useDispatch, useSelector } from 'react-redux';
import { getUser } from 'speed-js-react';
import type { UUID } from 'esm-web-pro/types/common';
import { setLandingTab, setLandingTabInSameBrowser } from 'esm-web-pro/redux/landingTab/actions';
import { getIsLandingTab } from 'esm-web-pro/redux/landingTab/selectors';
import { ApiClient } from 'speed-js-core';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import { removeUser, setPermissions } from 'speed-js-react/src/redux/auth/actions';
import { useHistory } from 'react-router';
import { LANDING_TAB_UNREGISTERED_LC_KEY } from 'esm-web-pro/api/auth/AuthLocalStorage';
import { FOCUSED_TAB_LC_KEY } from 'esm-web-pro/containers/panels/calling/FocusedTabsController';

type Props = {
    ws: WebSocketClient,
    logout: (data: Object) => Promise<Object>,
};

export const getLandingTabWSGroup = (user: Object) => `landing.tab.${user.id}`;
export const LANDING_TAB_WS_COMMAND = 'landing-tab';

// Key which gets added to & removed from the local storage only by the landing tab
// Used to determine in other tabs whether the landing tab is in the same browser or not
export const LANDING_TAB_LC_KEY = 'landing-tab';


type WSData = {
    landing_tab: {
        connection: UUID
    }
} | {
    unregistered_landing_tab: {
        connection: UUID,
    }
}

let keepLandingTabInterval = null;
const LandingTabController = (props: Props) => {
    const { ws, logout } = props;
    const history = useHistory();
    const dispatch = useDispatch();

    const user = useSelector(getUser);
    const isLandingTab = useSelector(getIsLandingTab);

    const landingTabRefreshTimeout = React.useRef(null);
    const clearNotifyInterval = () => {
        if (keepLandingTabInterval) {
            clearInterval(keepLandingTabInterval);
            keepLandingTabInterval = null;
        }
    };

    const wsGroup = user ? getLandingTabWSGroup(user) : null;

    const unregisterLandingTab = React.useCallback(() => {
        ws.send(LANDING_TAB_WS_COMMAND, {
            action: 'unregistered_landing_tab',
        });
        // Remove the array with focused tabs
        window.localStorage.removeItem(FOCUSED_TAB_LC_KEY);

        // Remove the indication that the landing tab is in this browser
        window.localStorage.removeItem(LANDING_TAB_LC_KEY);

        // Add a key to the local storage which flags that the landing tab has been unregistered
        // The key is used by the AuthLocalStorage provider in order to check whether the user should
        // log in automatically or not
        window.localStorage.setItem(LANDING_TAB_UNREGISTERED_LC_KEY,
            ws.connectionId);
        dispatch(setLandingTab(null));
        clearNotifyInterval();
    }, [dispatch, ws]);

    const handleWorkingTabLogout = React.useCallback(() => {
        logout().then(() => {
            ws.reconnect();
            history.push('/');
            dispatch(setPermissions({}));
            dispatch(removeUser());
        });
    }, [dispatch, logout, history]);

    const handleData = React.useCallback((data: WSData) => {
        // Event for a registration of a landing tab
        if (data.landing_tab) {
            // Remove the flag that the landing tab is unregistered
            window.localStorage.removeItem(LANDING_TAB_UNREGISTERED_LC_KEY);

            // The landing tab is the same as the current tab
            if (data.landing_tab.connection === ws.connectionId) {
                dispatch(setLandingTab(true));
                keepLandingTabInterval = setInterval(() => {
                    ws.send(LANDING_TAB_WS_COMMAND, {
                        action: 'keep_landing_tab',
                    });
                }, 45 * 1000);
                // Set the 'landing tab' key in the local storage -> this is used in the working tabs to detect
                // whether the landing is in the same browser or not
                window.localStorage.setItem(LANDING_TAB_LC_KEY, data.landing_tab.connection);
                window.addEventListener('unload', unregisterLandingTab);

                // The landing tab has a different connectionId -> this is a working tab
            } else if (data.landing_tab.connection || (data.landing_tab.connection === null
                && ws.connectionId !== window.localStorage.getItem(LANDING_TAB_LC_KEY))) {
                landingTabRefreshTimeout.current = null;
                dispatch(setLandingTab(false));
                clearNotifyInterval();

                const lcLandingTab = window.localStorage.getItem(LANDING_TAB_LC_KEY);

                if (data.landing_tab.connection && lcLandingTab !== data.landing_tab.connection) {
                    localStorage.removeItem(LANDING_TAB_LC_KEY);
                }
                // If there is a key in the local storage for the landing tab registration,
                // it means that the landing tab is in the same browser
                if (window.localStorage.getItem(LANDING_TAB_LC_KEY)) {
                    dispatch(setLandingTabInSameBrowser(true));
                }
            }
        } else if (data.unregistered_landing_tab
            && data.unregistered_landing_tab.connection !== ws.connectionId) {
            // Start a timeout -> if there isn't any message for a registration of a landing tab,
            // logout the user
            landingTabRefreshTimeout.current = true;

            // Set the "unregistered landing tab" also when an event for the "unregistration" is received
            // so that the user can log out even if the landing tab was not in the same browser
            window.localStorage.setItem(LANDING_TAB_UNREGISTERED_LC_KEY,
                data.unregistered_landing_tab.connection);
            setTimeout(() => {
                if (landingTabRefreshTimeout.current) {
                    // Logout is performed only by the "working" tabs after a given timeout in order to detect
                    // whether the landing tab is actually closed or has only been refreshed
                    handleWorkingTabLogout();
                }
            }, 10000);
        } else if (data.logout && data.logout.landing_tab !== ws.connectionId) {
            handleWorkingTabLogout();
        }
    }, [unregisterLandingTab, dispatch, handleWorkingTabLogout, ws]);

    const handleStorageUpdate = React.useCallback((event) => {
        if (event.key === LANDING_TAB_LC_KEY) {
            if (window.localStorage.getItem(LANDING_TAB_LC_KEY)) {
                dispatch(setLandingTabInSameBrowser(true));
            } else {
                dispatch(setLandingTabInSameBrowser(false));
            }
        }
    }, [dispatch]);


    React.useEffect(() => {
        if (wsGroup && isLandingTab === null) {
            ws.subscribe({
                [wsGroup]: handleData,
            });
            ws.onSubscribe(wsGroup, () => {
                ws.send(LANDING_TAB_WS_COMMAND, {
                    action: 'check_landing_tab',
                });
            });
        }
        window.addEventListener('storage', handleStorageUpdate);
        return () => {
            if (wsGroup && isLandingTab !== null) {
                ws.unSubscribe([wsGroup]);
            }
            window.removeEventListener('storage', handleStorageUpdate);
        };
    }, [ws, wsGroup, handleData, isLandingTab, handleStorageUpdate]);

    React.useEffect(() => {
        if (!isLandingTab && keepLandingTabInterval) {
            clearNotifyInterval();
        }
    }, [isLandingTab]);

    return null;
};

const apiCallback = (apiClient: ApiClient) => ({
    logout: {
        send: () => apiClient.deauthorize(),
    },
});

export default connectAPI(apiCallback)(connectWS(LandingTabController));
